import { useEffect } from 'react'

import { useCopilotChat } from '@copilotkit/react-core'
import { QuestionType } from '@factorialco/f0-react/dist/experimental'

import i18n from 'modules/core/lib/i18n'

import { useCocreationAgent } from 'modules/ai_reports/lib/useCocreationAgent'
import { FormEditor } from 'modules/forms/components/newFormEditor/formEditor'

import { AgentStateType, QuestionnaireType } from './types'

export const FormWithAgent = ({
  initialState,
  formType,
  savedState,
  reloadKey,
  setForm,
  allowedQuestionTypes,
  enabled,
}: {
  initialState: QuestionnaireType
  formType: AgentStateType['formType']
  savedState: QuestionnaireType | undefined
  reloadKey: number
  setForm: (form: QuestionnaireType) => void
  allowedQuestionTypes?: QuestionType[]
  enabled?: boolean
}) => {
  const { isLoading } = useCopilotChat()
  const { state, setState } = useCocreationAgent<AgentStateType, AgentStateType>({
    name: 'co-creation-workflow',
    initialState: {
      formType,
      questionnaire: initialState,
      updateForm: true,
    },
    initialMessage: i18n.t('forms.one.initial_message'),
    welcomeScreenSuggestions: [],
    placeholders: ['', ''], // temporal thing because at the moment we can't set different placeholders depending if we have messages in the chat or not
  })

  useEffect(() => {
    if (state?.questionnaire) {
      setForm(state.questionnaire)
    }
  }, [state, setForm])

  useEffect(() => {
    setForm(initialState)
  }, [initialState, setForm])

  // Sync title/description from initialState to agent state when they change externally
  useEffect(() => {
    if (setState && state?.questionnaire) {
      const currentTitle = state.questionnaire.title
      const currentDescription = state.questionnaire.description
      const newTitle = initialState.title
      const newDescription = initialState.description

      if (currentTitle !== newTitle || currentDescription !== newDescription) {
        setState({
          ...state,
          questionnaire: {
            ...state.questionnaire,
            title: newTitle,
            description: newDescription,
          },
          updateForm: true,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState.title, initialState.description])

  useEffect(() => {
    if (savedState && reloadKey > 0) {
      setState?.({ ...state, questionnaire: savedState, updateForm: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey])

  const handleChange = (questionnaire: QuestionnaireType) => {
    if (state) {
      setState?.({ ...state, questionnaire })
      setForm(questionnaire)
    }
  }

  if (!state) return null

  return (
    <FormEditor
      state={state.questionnaire}
      setState={handleChange}
      allowedQuestionTypes={allowedQuestionTypes}
      enabled={enabled}
      applyingChanges={isLoading}
    />
  )
}
